import React from 'react';
import { usePathGenerator } from '@cp/entities/clientWebRoutes';
import { useConfig } from '@cp/shared/config';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { ClientWebPageProps } from '@common/model/meta/clientWebPageProps';
import { coreCatalogApi } from '@cp/entities/catalog';
import { CATALOG_MIN_ITEMS_COUNT_FOR_INDEXING } from '@common/const/catalog';
import { PageMetaTags } from 'src/app/shared/components/layout/components/meta';
import { usePageMetaTags } from 'src/app/shared/hooks/useMetaTags';
import { useCatalogTemplateParameters } from 'src/widgets/catalog';
import { useHelmet } from 'src/shared/helmet';

export interface CatalogMetaTagsProps {
  pageProps: ClientWebPageProps[ClientWebPage.Catalog];
  listRequest: CatalogItemListRequest;
}

export const CatalogMetaTags: React.FC<CatalogMetaTagsProps> = ({ pageProps, listRequest }) => {
  const Helmet = useHelmet();
  const generatePath = usePathGenerator();
  const config = useConfig();
  const { data: catalogListResponse } = coreCatalogApi.endpoints.getItems.useQuery(listRequest);

  const paramsDictionary = useCatalogTemplateParameters({ listRequest });
  const renderConfig = usePageMetaTags({ dictionary: paramsDictionary });

  const canonicalHref = React.useMemo(() => {
    const path = generatePath({ page: ClientWebPage.Catalog, pageProps });
    return `${config.get('CLIENT_WEB_ORIGIN', '')}${path}`;
  }, [generatePath, pageProps, config]);
  const noindex = (catalogListResponse?.totalCount ?? 0) <= CATALOG_MIN_ITEMS_COUNT_FOR_INDEXING || (listRequest?.page || 1) > 1;
  const nofollow = (catalogListResponse?.totalCount ?? 0) <= CATALOG_MIN_ITEMS_COUNT_FOR_INDEXING;

  return (
    <>
      <PageMetaTags nofollow={nofollow} noindex={noindex} renderConfig={renderConfig} />
      <Helmet>
        <link href={canonicalHref} rel="canonical" />
      </Helmet>
    </>
  );
};
